import { DateTime, Interval } from "luxon";

import { DisabledDateRanges } from "./DateInput";

/** Given a ISO date, return true if the date is a weekday. */
export function isWeekday(date: string) {
  const weekday = DateTime.fromISO(date).weekday;

  return weekday !== 6 && weekday !== 7;
}

export function isWithinDateRange(date: string, dateRanges: DisabledDateRanges) {
  const isWithinDateRange = dateRanges.some((dateRange) => {
    const doesNotHaveBothDates = "start" in dateRange && "end" in dateRange;
    if (!doesNotHaveBothDates) return false;

    const interval = Interval.fromDateTimes(
      // Since we want to start date to be inclusive, we are comparing against
      // the start of the day of the start date.
      DateTime.fromISO(dateRange.start).startOf("day"),
      // Since we want to end date to be exclusive, we are comparing against
      // the start of the day of the end date.
      DateTime.fromISO(dateRange.end).startOf("day"),
    );

    return interval.contains(DateTime.fromISO(date));
  });

  return isWithinDateRange;
}

/**
 * Given the `disabledDateRanges` prop from the DateInput component, return the
 * simplified disabled date ranges for the IonDateTime component.
 */
export function transformDateRanges(disabledDateRanges?: DisabledDateRanges) {
  const firstItem = disabledDateRanges && disabledDateRanges.at(0);
  const lastItem = disabledDateRanges && disabledDateRanges.at(-1);
  // Covers first case in union
  const hasEnd = firstItem && "end" in firstItem && !("start" in firstItem);
  // Covers second case in union
  const hasStart = lastItem && "start" in lastItem && !("end" in lastItem);

  return {
    // let's make sure that we grab all of the items just in case
    min: hasEnd ? firstItem.end : undefined,
    // Since Ionic defined `max` as exclusive, whereas we defined as inclusive,
    // we must negate a day.
    max: hasStart
      ? (DateTime.fromISO(lastItem.start).minus({ day: 1 }).toISODate() ?? undefined)
      : undefined,
    // covers third and fourth cases in union
    disabledDateRanges: disabledDateRanges?.filter(
      (dateRange) =>
        "start" in dateRange &&
        dateRange.start?.length > 0 &&
        "end" in dateRange &&
        dateRange.end?.length > 0,
    ) as DisabledDateRanges,
  };
}
